import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { type Wallpaper, wallpapersCollection } from '../schema';
import { categoryService } from './categoryService';
import { wallpaperTransformService } from './wallpaperTransformService';

export interface CreateWallpaperData {
  title: string;
  description?: string;
  category: string;
  tags: string[];
  imageUrl: string;
  thumbnailUrl: string;
  resolutions: Array<{
    width: number;
    height: number;
    url: string;
    size: string;
  }>;
  categoryId?: string;
  // Device compatibility fields (new system only)
  primaryDevice?: import('../schema').DeviceType;
  supportedDevices?: import('../schema').DeviceType[];
}

/**
 * Service for admin-only wallpaper operations
 */
export class WallpaperAdminService {
  /**
   * Creates a new wallpaper (admin only)
   */
  async createWallpaper(data: CreateWallpaperData & { categoryId?: string }): Promise<Wallpaper> {
    try {
      // Check if user is authenticated
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to create wallpapers');
      }

      // Check admin permissions and get admin user data
      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to create wallpapers');
      }

      // Get the current admin user to set uploadedBy field
      const adminUser = await adminService.verifyAdminUser(currentUser.uid);
      const uploadedBy = adminUser?.userId || adminUser?.id || currentUser.uid;

      const now = new Date();

      // Generate search keywords
      const searchKeywords = wallpaperTransformService.generateSearchKeywords(
        data.title,
        data.description,
        data.category,
        data.tags
      );

      const wallpaperData: Omit<Wallpaper, 'id'> = {
        title: data.title,
        description: data.description || '',
        category: data.category,
        categoryId: data.categoryId,
        tags: data.tags,
        imageUrl: data.imageUrl,
        thumbnailUrl: data.thumbnailUrl,
        resolutions: data.resolutions,
        // Legacy deviceCompatibility field removed - using new device system only
        downloadCount: 0,
        uploadedBy: uploadedBy,
        createdAt: now,
        updatedAt: now,
        searchKeywords,
        // Persist filtering helpers when provided
        primaryDevice: data.primaryDevice,
        supportedDevices: data.supportedDevices,
      };

      const docRef = doc(wallpapersCollection);
      await setDoc(docRef, wallpaperData);

      // Update the category's wallpaper count if categoryId is provided
      if (data.categoryId) {
        await categoryService.incrementWallpaperCount(data.categoryId);
      }

      return {
        id: docRef.id,
        ...wallpaperData,
      };
    } catch (error) {
      console.error('Error creating wallpaper:', error);
      throw error instanceof Error ? error : new Error('Failed to create wallpaper');
    }
  }

  /**
   * Updates an existing wallpaper (admin only)
   */
  async updateWallpaper(id: string, data: Partial<CreateWallpaperData & { categoryId?: string }>): Promise<void> {
    try {
      // Check if user is authenticated
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to update wallpapers');
      }

      // Check admin permissions
      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to update wallpapers');
      }

      const docRef = doc(wallpapersCollection, id);
      const updates: Partial<Wallpaper> = {
        updatedAt: new Date()
      };

      // Get current data for category handling
      const currentData = (await getDoc(docRef)).data();

      if (currentData) {
        // Handle category change
        if (data.categoryId && data.categoryId !== currentData.categoryId) {
          // Decrement count for old category if it exists
          if (currentData.categoryId) {
            await categoryService.decrementWallpaperCount(currentData.categoryId);
          }
          // Increment count for new category
          await categoryService.incrementWallpaperCount(data.categoryId);
          updates.categoryId = data.categoryId;
        }

        // If title, description, or tags are updated, update search keywords
        if (data.title || data.description || data.tags) {
          const title = data.title || currentData.title;
          const description = data.description !== undefined ? data.description : currentData.description;
          const category = data.category || currentData.category;
          const tags = data.tags || currentData.tags;

          updates.searchKeywords = wallpaperTransformService.generateSearchKeywords(title, description, category, tags);
        }

        // Legacy device compatibility update removed - using new device system only
      }

      // Add other fields to updates
      Object.keys(data).forEach(key => {
        if (key !== 'categoryId' && data[key as keyof typeof data] !== undefined) {
          (updates as any)[key] = data[key as keyof typeof data];
        }
      });

      // Explicitly ensure primaryDevice / supportedDevices propagate if provided (already handled above but being explicit)
      if (data.primaryDevice !== undefined) {
        (updates as any).primaryDevice = data.primaryDevice;
      }
      if (data.supportedDevices !== undefined) {
        (updates as any).supportedDevices = data.supportedDevices;
      }

      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error updating wallpaper:', error);
      throw error instanceof Error ? error : new Error('Failed to update wallpaper');
    }
  }

  /**
   * Deletes a single wallpaper (admin only)
   */
  async deleteWallpaper(id: string): Promise<void> {
    try {
      console.log('🗑️ Starting wallpaper deletion for ID:', id);

      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to delete wallpapers');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to delete wallpapers');
      }

      const docRef = doc(wallpapersCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log('⚠️ Wallpaper not found:', id);
        throw new Error('Wallpaper not found');
      }

      const wallpaperData = docSnap.data() as Wallpaper;
      console.log('📄 Wallpaper data retrieved:', {
        title: wallpaperData.title,
        category: wallpaperData.category,
        categoryId: wallpaperData.categoryId
      });

      // Delete the document
      await deleteDoc(docRef);
      console.log('✅ Wallpaper document deleted from Firestore');

      // Update category count if category exists
      if (wallpaperData.categoryId) {
        console.log('📊 Decrementing category count for:', wallpaperData.categoryId);
        try {
          await categoryService.decrementWallpaperCount(wallpaperData.categoryId);
          console.log('✅ Category count decremented');
        } catch (categoryError) {
          console.warn('⚠️ Failed to decrement category count, but wallpaper was deleted:', categoryError);
          // Don't throw here - the wallpaper deletion was successful
        }
      } else {
        console.log('ℹ️ No categoryId found, skipping category count update');
      }

      console.log('🎉 Wallpaper deletion completed successfully');
    } catch (error) {
      console.error('❌ Error deleting wallpaper:', error);
      throw error instanceof Error ? error : new Error('Failed to delete wallpaper');
    }
  }

  /**
   * Batch delete multiple wallpapers (admin only)
   */
  async batchDeleteWallpapers(wallpaperIds: string[]): Promise<void> {
    try {
      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to delete wallpapers');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to delete wallpapers');
      }

      console.log(`🗑️ Starting batch deletion of ${wallpaperIds.length} wallpapers`);

      const categoryUpdates = new Map<string, number>();

      // Process each wallpaper
      for (const id of wallpaperIds) {
        try {
          const docRef = doc(wallpapersCollection, id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const wallpaperData = docSnap.data() as Wallpaper;

            // Track category updates
            if (wallpaperData.categoryId) {
              const currentCount = categoryUpdates.get(wallpaperData.categoryId) || 0;
              categoryUpdates.set(wallpaperData.categoryId, currentCount + 1);
            }

            // Delete the document
            await deleteDoc(docRef);
            console.log(`✅ Deleted wallpaper: ${id}`);
          } else {
            console.log(`⚠️ Wallpaper not found: ${id}`);
          }
        } catch (error) {
          console.error(`❌ Error deleting wallpaper ${id}:`, error);
          // Continue with other deletions
        }
      }

      // Update category counts
      for (const [categoryId, count] of categoryUpdates) {
        try {
          // Decrement category count multiple times for batch operation
          for (let i = 0; i < count; i++) {
            await categoryService.decrementWallpaperCount(categoryId);
          }
          console.log(`📊 Decremented category ${categoryId} by ${count}`);
        } catch (error) {
          console.error(`❌ Error updating category count for ${categoryId}:`, error);
        }
      }

      console.log('🎉 Batch deletion completed');
    } catch (error) {
      console.error('❌ Error in batch delete:', error);
      throw error instanceof Error ? error : new Error('Failed to batch delete wallpapers');
    }
  }

  /**
   * Increment download count for a wallpaper
   */
  async incrementDownloadCount(wallpaperId: string): Promise<void> {
    const docRef = doc(wallpapersCollection, wallpaperId);
    await updateDoc(docRef, {
      downloadCount: increment(1)
    });
  }

  /**
   * Like a wallpaper
   */
  async likeWallpaper(wallpaperId: string, userId: string): Promise<void> {
    const wallpaperRef = doc(wallpapersCollection, wallpaperId);
    await updateDoc(wallpaperRef, {
      likes: arrayUnion(userId)
    });
  }

  /**
   * Unlike a wallpaper
   */
  async unlikeWallpaper(wallpaperId: string, userId: string): Promise<void> {
    const wallpaperRef = doc(wallpapersCollection, wallpaperId);
    const wallpaperDoc = await getDoc(wallpaperRef);
    
    if (wallpaperDoc.exists()) {
      const data = wallpaperDoc.data() as Wallpaper;
      if (data.likes && data.likes.includes(userId)) {
        await updateDoc(wallpaperRef, {
          likes: arrayRemove(userId)
        });
      }
    }
  }
}

export const wallpaperAdminService = new WallpaperAdminService();